import ModalHeader from '@molecules/ModalHeader';
import ModalWindow from '@organisms/modals/ModalWindow';
import {FC, useEffect, useMemo, useState} from 'react';
import {
  ButtonsWrapper,
  ModalContent,
  ModalWrapper,
  SelectRoleTextBlock,
  SelectRoleWrapper,
} from './AddUserModal.styles';
import Button from '@atoms/Button';
import Input from '@atoms/Input';
import {useDispatch, useSelector} from 'react-redux';
import {system_roles, inviteUser, updateUser} from '@store/slices/users';
import {IRole, IUser} from '@types';
import RadioButton from '@atoms/RadioButton';
import {RadioButtons} from '@atoms/RadioButton.styles';
import Text from '@atoms/Text';
import {theme} from '@styles/theme';
import {AppDispatch, RootState} from '@store';
import Toast from '@atoms/Toast';
import {toast} from 'react-toastify';
import {EUserRoles} from "@constants/usersRoles";

interface Props {
  isOpened: boolean;
  onCloseRequest: () => void;
  user?: IUser;
}

const AddUserModal: FC<Props> = ({isOpened, onCloseRequest, user}) => {
  const systemRolesArr: IRole[] = useSelector(system_roles);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userRole, setUserRole] = useState<string>('');
  const dispatch = useDispatch<AppDispatch>();
  const currentUserSystemRole = useSelector(
    (state: RootState) => state.profile.profile?.role_id,
  );

  const filteredSystemRoles = useMemo(() => {
    if (currentUserSystemRole === EUserRoles.TECH_ADMIN) {
      return systemRolesArr;
    }
    return systemRolesArr.filter(r => r.id !== EUserRoles.TECH_ADMIN);
  }, [systemRolesArr, currentUserSystemRole]);

  useEffect(() => {
    if (user?.id) {
      setUserName(user.username);
      setUserEmail(user.email);
      setUserRole(String(user.role_id));
    } else {
      setUserEmail('');
      setUserName('');
      setUserRole('');
    }
  }, [user]);

  const AddUser = () => {
    const role = systemRolesArr.find(role => String(role.id) == userRole);
    if (user?.id) {
      dispatch(
        updateUser({
          id: user.id,
          email: userEmail,
          username: userName,
          role_id: role?.id || '3',
        }),
      );
    } else {
      dispatch(
        inviteUser({
          email: userEmail,
          username: userName,
          role_id: role?.id || '3',
        }),
      )
        .unwrap()
        .then(() => {
          toast.success(
            <Toast
              title="Готово"
              text={`Приглашение выслано на email\n${userEmail}`}
              type="success"
            />,
          );
          onCloseRequest();
          setUserEmail('');
          setUserName('');
          setUserRole('');
        })
        .catch((err: any) => {
          toast.error(
            <Toast
              title="Ошибка"
              text={err?.message || 'Не удалось отправить приглашение'}
              type="error"
            />,
          );
        });
      return;
    }
  };

  return (
    <ModalWindow isOpen={isOpened} overflowIsVisible>
      <ModalWrapper>
        <ModalHeader
          title={user?.id ? 'Редактировать' : 'Добавить пользователя'}
          onClose={onCloseRequest}
        />
        <ModalContent>
          <Input
            id="user-email"
            label="Email пользователя (login/ID)"
            value={userEmail}
            onChange={e => {
              setUserEmail(e.currentTarget.value);
            }}
            withoutErrorText
          />
          <Input
            id="user-name"
            label="Имя пользователя в системе"
            value={userName}
            onChange={e => {
              setUserName(e.currentTarget.value);
            }}
            withoutErrorText
          />
          <SelectRoleWrapper>
            <SelectRoleTextBlock>
              <Text text="Назначить роль" type="captionB" color={theme.colors.basic01} />
              <Text
                text="Замещающий текст: общее и краткое описание ответственности и ограничений в рамках конкретной роли пользователя."
                type="tinyR"
                color={theme.colors.basic01}
              />
            </SelectRoleTextBlock>
            <RadioButtons>
              {filteredSystemRoles &&
                filteredSystemRoles.map(role => {
                  return (
                    <RadioButton
                      key={`radio-button-role-${role.id}`}
                      id={`radio-button-role-${role.id}`}
                      onChange={e => setUserRole(e.target.value)}
                      label={role.name}
                      name="userRole"
                      value={String(role.id)}
                      checked={userRole === String(role.id)}
                    />
                  );
                })}
            </RadioButtons>
          </SelectRoleWrapper>
        </ModalContent>
        <ButtonsWrapper>
          <Button
            fullWidth
            title={user?.id ? 'Сохранить' : 'Добавить'}
            variant="filled"
            disabled={!userRole || !userEmail || !userName}
            onClick={AddUser}
          />
          <Button
            fullWidth
            title="Закрыть"
            onClick={() => {
              onCloseRequest();
              setUserName('');
              setUserEmail('');
              setUserRole('');
            }}
          />
        </ButtonsWrapper>
      </ModalWrapper>
    </ModalWindow>
  );
};

export default AddUserModal;
